import { GSplatData } from '@/plugins/gsplat/core/GSplatData'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type {
  GSplatHandler,
  GSplatLoadUrlObject,
  GSplatParserContext,
  IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import { GSplatResource } from '@/plugins/gsplat/unified/resource/GSplatResource'

const BYTES_PER_SPLAT = 32
const SH_C0 = 0.28209479177387814

export interface SplatParserOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly reorder?: boolean
  readonly prepareCenters?: boolean
  readonly onData?: (data: GSplatData) => void
  /** 下载完成并确认 record 边界后、展开完整 CPU/GPU 数据前执行。 */
  readonly validateSplatCount?: (count: number) => void
}

/**
 * 本地 `.splat` 32-byte record 到 PlayCanvas `GSplatData` 的窄适配。
 * 它只转换输入编码，不创建第二套 Resource、renderer 或 backend 分支。
 */
export function parseSplat(buffer: ArrayBuffer): GSplatData {
  if (buffer.byteLength % BYTES_PER_SPLAT !== 0) {
    throw new Error(
      `SplatParser: byteLength 必须为 ${BYTES_PER_SPLAT} 的整数倍，actual=${buffer.byteLength}`
    )
  }
  const count = buffer.byteLength / BYTES_PER_SPLAT
  const view = new DataView(buffer)
  const propertyNames = [
    'x',
    'y',
    'z',
    'f_dc_0',
    'f_dc_1',
    'f_dc_2',
    'opacity',
    'scale_0',
    'scale_1',
    'scale_2',
    'rot_0',
    'rot_1',
    'rot_2',
    'rot_3',
  ] as const
  const properties = Object.fromEntries(
    propertyNames.map((name) => [name, new Float32Array(count)])
  ) as Record<(typeof propertyNames)[number], Float32Array>

  for (let index = 0; index < count; index++) {
    const offset = index * BYTES_PER_SPLAT
    properties.x[index] = view.getFloat32(offset, true)
    properties.y[index] = view.getFloat32(offset + 4, true)
    properties.z[index] = view.getFloat32(offset + 8, true)
    properties.scale_0[index] = Math.log(view.getFloat32(offset + 12, true))
    properties.scale_1[index] = Math.log(view.getFloat32(offset + 16, true))
    properties.scale_2[index] = Math.log(view.getFloat32(offset + 20, true))
    properties.f_dc_0[index] = (view.getUint8(offset + 24) / 255 - 0.5) / SH_C0
    properties.f_dc_1[index] = (view.getUint8(offset + 25) / 255 - 0.5) / SH_C0
    properties.f_dc_2[index] = (view.getUint8(offset + 26) / 255 - 0.5) / SH_C0
    const alpha = view.getUint8(offset + 27) / 255
    properties.opacity[index] = alpha <= 0 ? -40 : alpha >= 1 ? 40 : -Math.log(1 / alpha - 1)
    properties.rot_0[index] = (view.getUint8(offset + 28) - 128) / 128
    properties.rot_1[index] = (view.getUint8(offset + 29) - 128) / 128
    properties.rot_2[index] = (view.getUint8(offset + 30) - 128) / 128
    properties.rot_3[index] = (view.getUint8(offset + 31) - 128) / 128
  }

  return new GSplatData([
    {
      name: 'vertex',
      count,
      properties: propertyNames.map((name) => ({
        name,
        type: 'float',
        storage: properties[name],
        byteSize: Float32Array.BYTES_PER_ELEMENT,
      })),
    },
  ])
}

export class SplatParser implements IGSplatParser<GSplatResource<GSplatData>, SplatParserOptions> {
  handler?: GSplatHandler<GSplatResource<GSplatData>, SplatParserOptions> | null

  canParse(context: GSplatParserContext<SplatParserOptions>): boolean {
    return context.ext === 'splat'
  }

  async load(
    url: GSplatLoadUrlObject,
    context: GSplatParserContext<SplatParserOptions>
  ): Promise<GSplatResource<GSplatData>> {
    const options = context.options
    if (!options?.adapter) throw new Error('SplatParser: 缺少 GSplatGraphicsAdapter')
    let buffer: ArrayBuffer
    if (url.load instanceof Blob) {
      buffer = await url.load.arrayBuffer()
    } else {
      const response = await fetch(
        url.load,
        context.signal ? { signal: context.signal } : undefined
      )
      if (!response.ok) {
        throw new Error(`SplatParser: 加载失败 ${response.status} ${response.statusText}`)
      }
      buffer = await response.arrayBuffer()
    }
    if (context.signal?.aborted) throw new DOMException('SplatParser load 已取消', 'AbortError')
    if (buffer.byteLength % BYTES_PER_SPLAT !== 0) {
      throw new Error(
        `SplatParser: byteLength 必须为 ${BYTES_PER_SPLAT} 的整数倍，actual=${buffer.byteLength}`
      )
    }
    options.validateSplatCount?.(buffer.byteLength / BYTES_PER_SPLAT)
    const data = parseSplat(buffer)
    options.onData?.(data)
    if (options.reorder ?? false) data.reorderData()
    return GSplatResource.create(options.adapter, data, {
      prepareCenters: options.prepareCenters,
    })
  }
}
